import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  AlertTriangle, 
  TrendingUp, 
  Users, 
  DollarSign,
  CheckCircle,
  ArrowUpRight,
  FileText,
  ThumbsUp,
  XCircle
} from 'lucide-react';
import { TimelineEvent, SpotBonus } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

export default function ChronologyPage() {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [bonuses, setBonuses] = useState<SpotBonus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventsResponse, bonusesResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/timeline-events`),
        fetch(`${API_BASE_URL}/api/spot-bonuses`)
      ]);

      if (!eventsResponse.ok || !bonusesResponse.ok) {
        throw new Error('Failed to fetch data');
      }

      const [eventsData, bonusesData] = await Promise.all([
        eventsResponse.json(),
        bonusesResponse.json()
      ]);

      setEvents(eventsData);
      setBonuses(bonusesData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Event type styles based on chronology_v2.html
  const getEventTypeStyles = (eventType: string) => {
    const typeStyles: { [key: string]: { icon: React.ReactNode; borderColor: string; bgColor: string; size: 'small' | 'normal' } } = {
      milestone: { 
        icon: <CheckCircle className="h-6 w-6 text-green-400" />, 
        borderColor: "border-green-500", 
        bgColor: "bg-green-500/10 hover:bg-green-500/20", 
        size: 'normal' 
      },
      change: { 
        icon: <ArrowUpRight className="h-6 w-6 text-blue-400" />, 
        borderColor: "border-blue-500", 
        bgColor: "bg-blue-500/10 hover:bg-blue-500/20", 
        size: 'normal' 
      },
      warning: { 
        icon: <AlertTriangle className="h-6 w-6 text-yellow-400" />, 
        borderColor: "border-yellow-500", 
        bgColor: "bg-yellow-500/10 hover:bg-yellow-500/20", 
        size: 'normal' 
      },
      termination: { 
        icon: <XCircle className="h-6 w-6 text-red-400" />, 
        borderColor: "border-red-500", 
        bgColor: "bg-red-500/10 hover:bg-red-500/20", 
        size: 'normal' 
      },
      bonus: { 
        icon: <DollarSign className="h-5 w-5 text-teal-400" />, 
        borderColor: "border-teal-500", 
        bgColor: "bg-teal-500/10 hover:bg-teal-500/20", 
        size: 'small' 
      },
      kudos: { 
        icon: <ThumbsUp className="h-5 w-5 text-purple-400" />, 
        borderColor: "border-purple-500", 
        bgColor: "bg-purple-500/10 hover:bg-purple-500/20", 
        size: 'small' 
      },
      promotion: { 
        icon: <TrendingUp className="h-6 w-6 text-cyan-400" />, 
        borderColor: "border-cyan-500", 
        bgColor: "bg-cyan-500/10 hover:bg-cyan-500/20", 
        size: 'normal' 
      },
      review: { 
        icon: <FileText className="h-6 w-6 text-orange-400" />, 
        borderColor: "border-orange-500", 
        bgColor: "bg-orange-500/10 hover:bg-orange-500/20", 
        size: 'normal' 
      },
      performance_review: { 
        icon: <FileText className="h-6 w-6 text-orange-400" />, 
        borderColor: "border-orange-500", 
        bgColor: "bg-orange-500/10 hover:bg-orange-500/20", 
        size: 'normal' 
      },
      manager_change: { 
        icon: <Users className="h-6 w-6 text-blue-400" />, 
        borderColor: "border-blue-500", 
        bgColor: "bg-blue-500/10 hover:bg-blue-500/20", 
        size: 'normal' 
      },
      audit: { 
        icon: <AlertTriangle className="h-6 w-6 text-yellow-400" />, 
        borderColor: "border-yellow-500", 
        bgColor: "bg-yellow-500/10 hover:bg-yellow-500/20", 
        size: 'normal' 
      },
      ethical_conflict: { 
        icon: <AlertTriangle className="h-6 w-6 text-red-400" />, 
        borderColor: "border-red-500", 
        bgColor: "bg-red-500/10 hover:bg-red-500/20", 
        size: 'normal' 
      },
      negative_action: { 
        icon: <AlertTriangle className="h-6 w-6 text-red-400" />, 
        borderColor: "border-red-500", 
        bgColor: "bg-red-500/10 hover:bg-red-500/20", 
        size: 'normal' 
      }
    };
    
    return typeStyles[eventType] || typeStyles.change;
  };

  const toggleEventExpansion = (eventId: number) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  // Filter events based on selected criteria
  const filteredEvents = events.filter(event => {
    // Year filter
    if (selectedYear && new Date(event.event_date).getFullYear() !== selectedYear) {
      return false;
    }
    
    // Event type filter
    if (selectedEventType && event.event_type !== selectedEventType) {
      return false;
    }
    
    // Date range filter
    if (dateRange.start || dateRange.end) {
      const eventDate = new Date(event.event_date);
      if (dateRange.start && eventDate < new Date(dateRange.start)) {
        return false;
      }
      if (dateRange.end && eventDate > new Date(dateRange.end)) {
        return false;
      }
    }
    
    return true;
  });

  const years = Array.from(new Set(events.map(event => new Date(event.event_date).getFullYear()))).sort((a, b) => b - a);
  const eventTypes = Array.from(new Set(events.map(event => event.event_type))).sort();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading chronology...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 shadow-sm border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-100">Employee Career Timeline</h1>
                <p className="text-sm text-gray-400">A visualization of key career and project events</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-400">
                {filteredEvents.length} events
              </div>
            </div>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-4 pb-6">
            <select
              value={selectedYear || ''}
              onChange={(e) => setSelectedYear(e.target.value ? parseInt(e.target.value) : null)}
              className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Years</option>
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            
            <select
              value={selectedEventType}
              onChange={(e) => setSelectedEventType(e.target.value)}
              className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Event Types</option>
              {eventTypes.map(type => (
                <option key={type} value={type}>{type.replace('_', ' ')}</option>
              ))}
            </select>
            
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Start Date"
            />
            
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="End Date"
            />
            
            <button
              onClick={() => {
                setSelectedYear(null);
                setSelectedEventType('');
                setDateRange({ start: '', end: '' });
              }}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gray-600 z-0"></div>
          
          <div className="space-y-8">
            {filteredEvents.map((event, index) => {
              const styles = getEventTypeStyles(event.event_type);
              const isExpanded = expandedEvents.has(event.event_id);
              const isLeft = index % 2 === 0;
              
              return (
                <div key={event.event_id} className={`relative flex ${isLeft ? 'justify-start' : 'justify-end'}`}>
                  {/* Event Card */}
                  <div className={`w-5/12 ${isLeft ? 'pr-8' : 'pl-8'}`}>
                    <div 
                      className={`rounded-lg shadow-xl transition duration-300 ease-in-out transform hover:scale-105 cursor-pointer ${styles.bgColor} ${styles.borderColor} border-l-4 ${styles.size === 'small' ? 'px-4 py-2' : 'px-6 py-4'}`}
                      onClick={() => toggleEventExpansion(event.event_id)}
                    >
                      <div className="flex justify-between items-center mb-1">
                        <p className="text-sm font-medium text-gray-400">
                          {new Date(event.event_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                        <span className="text-xs font-medium text-gray-400 capitalize">
                          {event.event_type.replace('_', ' ')}
                        </span>
                      </div>
                      
                      <h3 className="font-bold text-gray-100 text-lg mb-2">{event.title}</h3>
                      
                      {event.description && (
                        <div className={`transition-all duration-500 overflow-hidden ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                          <p className="text-sm text-gray-300 leading-relaxed">
                            {event.description}
                          </p>
                        </div>
                      )}
                      
                      {event.metadata && (
                        <div className={`transition-all duration-500 overflow-hidden ${isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                          <div className="mt-2 p-2 bg-gray-800 rounded text-xs text-gray-400">
                            <strong>Metadata:</strong> {JSON.stringify(event.metadata)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Timeline Icon */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 z-10 bg-gray-800 shadow-xl rounded-full flex items-center justify-center" 
                       style={{ width: styles.size === 'small' ? '40px' : '48px', height: styles.size === 'small' ? '40px' : '48px' }}>
                    {styles.icon}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {filteredEvents.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-100">No events found</h3>
            <p className="mt-1 text-sm text-gray-400">
              {selectedYear || selectedEventType || dateRange.start || dateRange.end
                ? 'No events match your current filters.'
                : 'No timeline events have been recorded yet.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Spot Bonuses Summary */}
      {bonuses.length > 0 && (
        <div className="bg-gray-800 border-t border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <h2 className="text-lg font-semibold text-gray-100 mb-4">Spot Bonuses</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bonuses.map((bonus) => (
                <div key={bonus.bonus_id} className="bg-teal-500/10 border border-teal-500 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-teal-300">
                        ${bonus.amount.toLocaleString()}
                      </p>
                      <p className="text-xs text-teal-400">
                        {new Date(bonus.bonus_date).toLocaleDateString()}
                      </p>
                    </div>
                    <DollarSign className="h-6 w-6 text-teal-400" />
                  </div>
                  {bonus.reason && (
                    <p className="mt-2 text-xs text-teal-200">{bonus.reason}</p>
                  )}
                  {bonus.manager_name && (
                    <p className="mt-1 text-xs text-teal-300">From: {bonus.manager_name}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
